"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import BottomCta from "@/components/BottomCta";
import { Shield, FileText, BadgeCheck, Zap, Users, CheckCircle2, TrendingUp, Award } from "lucide-react";

const SERVICES = [
  { icon: BadgeCheck, title: "Eligibility Assessment", desc: "Complete profile review against all UAE Golden Visa categories and eligibility criteria." },
  { icon: FileText, title: "Documentation Management", desc: "Full handling of all certificates, attestations, legal paperwork and approvals." },
  { icon: Shield, title: "Application Preparation", desc: "Professional preparation and organization of all required documentation." },
  { icon: Zap, title: "Fast-Track Processing", desc: "Priority submission and direct coordination with UAE immigration authorities." },
  { icon: Award, title: "Visa Issuance Support", desc: "Complete support from approval to Emirates ID delivery and residency activation." },
  { icon: Users, title: "Family & Sponsor Support", desc: "End-to-end assistance for family members and sponsor coordination." },
];

const STEPS = [
  { num: "01", title: "Consultation & Assessment", desc: "We review your profile and determine the best Golden Visa pathway for your situation." },
  { num: "02", title: "Documentation Preparation", desc: "Hassle-free management of all legal paperwork, certificates and compliance requirements." },
  { num: "03", title: "Application & Submission", desc: "Official submission and direct coordination with UAE immigration authorities." },
  { num: "04", title: "Approval & Activation", desc: "Visa processing, Emirates ID issuance and full residency activation support." },
];

const WHY_CHOOSE = [
  "98% approval rate with expert application handling",
  "Complete eligibility assessment and pathway planning",
  "Fast-track processing with priority authority coordination",
  "End-to-end support from consultation to residency activation",
  "Personalized solutions for investors and professionals",
];

export default function GoldenVisaPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              UAE Residency
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Golden Visa UAE <br />
            <span className="font-serif font-normal text-[#B8893A]">Long-Term Residency</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            End-to-end support for eligibility assessment, documentation, and approval. We help investors, professionals, and entrepreneurs secure long-term UAE residency with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link
              href="/contact"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Get Started
            </Link>
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Talk to Experts
            </a>
          </motion.div>
        </div>
      </section>

      {/* WHY GOLDEN VISA MATTERS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Golden Visa Matters</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Long-term security and stability</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              The Golden Visa provides 5-10 year residency valid for investors, professionals, and entrepreneurs. Perfect for securing long-term UAE residency without visa sponsorship complexity.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">What You Get</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Complete application support</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Expert guidance from eligibility assessment through documentation, application, and approval. We handle everything so you can focus on your life in the UAE.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">What We Offer</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Complete Golden Visa services</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {SERVICES.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple. Clear. Efficient.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-8 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Golden Visa experts you can trust</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              With a 98% approval rate and 500+ successful applications, we provide expert guidance and complete support for your UAE Golden Visa journey.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Start Your Golden Visa Journey Today"
        subtitle="Get a personalized eligibility assessment and discover the best residency pathway for your situation. Free consultation with no obligation."
      />
    </main>
  );
}
